import { Injectable, inject } from '@angular/core';
import { ExamTabLockService } from './exam-tab-lock.service';
import { MockTestSecurityService } from './mock-test-security.service';
import { detectPrivateBrowsing } from './private-browsing.util';

type ExtendedScreen = Screen & { isExtended?: boolean };

/**
 * Pre-start checks shared by every "start / resume test" entry point. The
 * multi-tab and multi-monitor checks only apply when the exam has proctoring
 * enabled; the rest are enforced regardless. Devtools detection combines the
 * window-size heuristic (docked devtools) with console timing (undocked
 * devtools and device emulation, where the size heuristic sees no gap).
 */
@Injectable({ providedIn: 'root' })
export class ExamEnvironmentCheckService {
  private readonly securityService = inject(MockTestSecurityService);
  private readonly tabLock = inject(ExamTabLockService);

  /** Returns one user-facing message per problem; empty when the exam may start. */
  async findIssues(enableProctoring: boolean): Promise<string[]> {
    const issues: string[] = [];
    const [otherTabCount, isPrivateBrowsing] = await Promise.all([
      enableProctoring ? this.tabLock.probe().then((result) => result.otherTabCount) : 0,
      detectPrivateBrowsing(),
    ]);

    // A private window can't see the other tabs, so the tab check below (and
    // the running-exam tab lock) would miss an exam open in a normal window.
    if (isPrivateBrowsing) {
      issues.push(
        'Private / incognito browsing is not supported. Please open the test in a normal browser window.',
      );
    }

    if (otherTabCount > 0) {
      issues.push(
        `Multiple tabs are not supported. ${otherTabCount} other tab${otherTabCount > 1 ? 's' : ''} of this website ${otherTabCount > 1 ? 'are' : 'is'} open. Please close the duplicate tab${otherTabCount > 1 ? 's' : ''} before starting the test.`,
      );
    }

    // screen.isExtended is Chromium-only (Chrome / Edge 100+) and needs no
    // permission prompt; other browsers leave it undefined, so they pass.
    // Mirrored displays count as one screen.
    // if (enableProctoring && (screen as ExtendedScreen).isExtended) {
    //   issues.push(
    //     'Multiple monitors detected. Using more than one monitor is a clear violation of the exam rules. Please disconnect the additional monitor(s) before starting the test.',
    //   );
    // }
    // Devtools restriction temporarily disabled.
    // if (this.securityService.isDevtoolsOpen() || this.securityService.isDevtoolsConsoleAttached()) {
    //   issues.push('Developer tools are open. Please close the developer tools to start the test.');
    // }

    return issues;
  }
}
